import { useState, useMemo } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { EmptyState } from '../common/EmptyState';
import type { CertAlert } from '../../types';

interface CertificationsTabProps {
  certs: CertAlert[];
}

type CertFilter = 'all' | 'expiring' | 'expired' | 'valid';

const TH: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase',
  letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left',
  borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap',
};

const TD: React.CSSProperties = {
  fontSize: 13, color: palette.g700, padding: '10px 12px',
  borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap',
};

function certStatusStyle(status: string): { bg: string; color: string } {
  if (status === 'expired') return { bg: palette.redWash, color: palette.red };
  if (status === 'expiring') return { bg: palette.amberWash, color: palette.amber };
  return { bg: palette.greenWash, color: palette.green };
}

function typeLabel(type: string): string {
  const map: Record<string, string> = { licence: 'Licence', certification: 'Certification', insurance: 'Insurance', training: 'Training', other: 'Other' };
  return map[type] ?? type;
}

export function CertificationsTab({ certs }: CertificationsTabProps) {
  const [filter, setFilter] = useState<CertFilter>('all');

  const urgentCount = certs.filter(c => c.status === 'expired' || c.status === 'expiring').length;

  const filtered = useMemo(() => {
    if (filter === 'all') return certs;
    if (filter === 'expiring') return certs.filter(c => c.status === 'expiring');
    if (filter === 'expired') return certs.filter(c => c.status === 'expired');
    return certs.filter(c => c.status === 'valid');
  }, [certs, filter]);

  const filters: { key: CertFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'expiring', label: 'Expiring Soon' },
    { key: 'expired', label: 'Expired' },
    { key: 'valid', label: 'Valid' },
  ];

  return (
    <div>
      {/* Alert banner */}
      {urgentCount > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 16px', borderRadius: 8, marginBottom: 12,
          background: palette.amberWash, borderLeft: `3px solid ${palette.amber}`,
        }}>
          <AlertTriangle size={16} color={palette.amber} />
          <span style={{ fontSize: 13, color: palette.amber, fontWeight: 500 }}>
            {urgentCount} certification{urgentCount > 1 ? 's' : ''} expired or expiring soon
          </span>
        </div>
      )}

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 6, padding: 2, marginBottom: 16, width: 'fit-content' }}>
        {filters.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '4px 12px', borderRadius: 5, border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
            background: filter === f.key ? palette.g800 : 'transparent',
            color: filter === f.key ? palette.white : palette.g500,
            transition: 'all 0.12s',
          }}>{f.label}</button>
        ))}
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>Team member</th>
              <th style={TH}>Certification</th>
              <th style={TH}>Type</th>
              <th style={TH}>Expires</th>
              <th style={TH}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 0 }}><EmptyState icon={<ShieldCheck size={22} />} title="No certifications found" subtitle="Certifications uploaded by your team will appear here." /></td></tr>
            ) : filtered.map(c => {
              const ss = certStatusStyle(c.status);
              return (
                <tr key={c.id}
                  style={{ transition: 'background 150ms ease' }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ ...TD, fontWeight: 500, color: palette.g800 }}>{c.userName}</td>
                  <td style={TD}>{c.certName}</td>
                  <td style={TD}>{typeLabel(c.certType)}</td>
                  <td style={TD}>{c.expiryDate ? new Date(c.expiryDate + 'T00:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : 'No expiry'}</td>
                  <td style={TD}>
                    <span style={{
                      fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 4,
                      background: ss.bg, color: ss.color, textTransform: 'capitalize',
                    }}>{c.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
